import { useEffect, useState } from 'react';
import {
  bulkUpdateOrder,
  fetchItemCascadePreview,
  OrderConflictError,
  type ItemCascadePreview,
} from '../../lib/orders';
import { formatCurrency } from '../../lib/format';
import { ConfirmDialog } from '../offerte-panel/ConfirmDialog';

interface Props {
  isOpen: boolean;
  wiId: string;
  orderId: string;
  onClose: () => void;
  onDeleted: (nieuweLastModifiedDate: string | null) => void;
}

function formatDatum(iso: string | null): string {
  if (!iso) return 'onbekende datum';
  try {
    return new Date(iso).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
    });
  } catch {
    return iso;
  }
}

function buildBody(preview: ItemCascadePreview): string {
  const lines: string[] = [];
  lines.push(
    `Verwijder "${preview.primary.name}" (${formatCurrency(preview.primary.priceInclBtw)}).`
  );

  if (preview.behandelingen.length > 0) {
    const namen = preview.behandelingen.map((b) => b.name).join(', ');
    lines.push(
      `+ ${preview.behandelingen.length} gekoppelde behandeling${
        preview.behandelingen.length === 1 ? '' : 'en'
      }: ${namen}`
    );
  }

  if (preview.reparateurRits.length > 0) {
    for (const rit of preview.reparateurRits) {
      const verb = rit.wordtLeegNaDelete ? 'wordt geannuleerd' : 'wordt ontkoppeld';
      lines.push(
        `Reparateur-stop op ${formatDatum(rit.datum)} (${rit.type}) ${verb}.`
      );
    }
  }

  lines.push('');
  lines.push(`Totaal-impact: −${formatCurrency(preview.totaalImpactInclBtw)}`);

  if (preview.isInvoiced) {
    lines.push('');
    lines.push(
      '⚠ Order is gefactureerd — totaalbedrag wijzigt; controleer de factuur.'
    );
  }

  return lines.join('\n');
}

function buildConfirmLabel(preview: ItemCascadePreview): string {
  const cancelsRit = preview.reparateurRits.some((r) => r.wordtLeegNaDelete);
  if (cancelsRit) return 'Verwijder + cancel reparateur-stop';
  if (preview.behandelingen.length > 0) {
    return `Verwijder item + ${preview.behandelingen.length} behandeling${
      preview.behandelingen.length === 1 ? '' : 'en'
    }`;
  }
  return 'Verwijder item';
}

export function DeleteItemDialog({ isOpen, wiId, orderId, onClose, onDeleted }: Props) {
  const [preview, setPreview] = useState<ItemCascadePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setPreview(null);
      setError(null);
      setBusy(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchItemCascadePreview(wiId)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Preview mislukt');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, wiId]);

  if (!isOpen) return null;

  if (loading || !preview) {
    return (
      <ConfirmDialog
        isOpen
        title="Item verwijderen"
        body={error ?? 'Cascade laden…'}
        confirmLabel="Verwijder"
        onConfirm={() => {}}
        onCancel={onClose}
        confirmVariant="danger"
        isBusy
      />
    );
  }

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await bulkUpdateOrder(orderId, [
        { kind: 'remove', itemId: wiId },
      ]);
      onDeleted(result.nieuweLastModifiedDate);
      onClose();
    } catch (err) {
      if (err instanceof OrderConflictError) {
        setError(
          'Deze order is ondertussen gewijzigd. Sluit de dialog en ververs de pagina.'
        );
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Verwijderen mislukt');
      }
    } finally {
      setBusy(false);
    }
  };

  const body = error ? `${buildBody(preview)}\n\n⚠ ${error}` : buildBody(preview);

  return (
    <ConfirmDialog
      isOpen
      title="Item verwijderen"
      body={body}
      confirmLabel={buildConfirmLabel(preview)}
      onConfirm={handleConfirm}
      onCancel={onClose}
      confirmVariant="danger"
      isBusy={busy}
    />
  );
}
